import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { PessoaDTO, PessoaFormDTO } from '../../../../models/clientes/pessoa.model';
import { TipoPessoa } from '../../../../enums/tipoPessoa.enum';
import { TipoNaturezaFiscal } from '../../../../enums/tipoNaturezaFiscal.enum';
import { Sexo } from '../../../../enums/sexo.enum';
import { CidadeService } from '../../../../services/configuracoes/cidade.service';
import { PessoaService } from '../../../../services/pessoas/pessoas.service';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { PaisDDI } from '../../../../models/cardapio-digital/cardapio-digital.model';
import { PAISES_DDI } from '../../../../shared/data/paises-ddi.data';

@Component({
  selector: 'app-cliente-edit',
  templateUrl: './cliente-edit.component.html',
  styleUrls: ['./cliente-edit.component.scss'],
  providers: [MessageService],
})
export class ClienteEditComponent {
  form: PessoaFormDTO = this.createEmptyForm();
  submitting = false;
  cidadeList: { label: string; value: string; estado: string }[] = [];
  tipoPessoaOptions: { value: TipoPessoa; label: string }[] = [];
  tipoNaturezaFiscalOptions: { value: TipoNaturezaFiscal; label: string }[] = [];
  sexoOptions: { value: Sexo; label: string }[] = [];
  estadoCidade!: string;

  // DDI dropdown
  paisSelecionado: PaisDDI = PAISES_DDI[0];
  cidade!: { label: string; value: string };

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private pessoaService: PessoaService,
    private cidadeService: CidadeService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  private langChangeSub!: Subscription;

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadCliente(Number(id));
    }

    this.buildTipoPessoaOptions();
    this.buildTipoNaturezaFiscalOptions();
    this.buildSexoOptions();
    // Atualiza labels quando idioma muda
    this.langChangeSub = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.buildTipoPessoaOptions();
      this.buildTipoNaturezaFiscalOptions();
      this.buildSexoOptions();
    });

    this.loadDropdowns();
  }

  private buildTipoPessoaOptions() {
    this.tipoPessoaOptions = Object.values(TipoPessoa).map(tipo => ({
      value: tipo,
      label: this.translate.instant(`enums.tipo_pessoa.${tipo.toLowerCase()}`)
    }));
  }

  private buildTipoNaturezaFiscalOptions() {
    this.tipoNaturezaFiscalOptions = Object.values(TipoNaturezaFiscal).map(natureza => ({
      value: natureza,
      label: this.translate.instant(`enums.tipo_natureza_fiscal.${natureza.toLowerCase()}`)
    }));
  }

  private buildSexoOptions() {
    this.sexoOptions = Object.values(Sexo).map(sexo => ({
      value: sexo,
      label: this.translate.instant(`enums.sexo.${sexo.toLowerCase()}`)
    }));
  }

  onSelecionarCidade(event: any) {
    // O showClear do p-dropdown emite onChange com value null: sem cidade, o estado tambem some.
    const valorCidade = event?.value?.value;
    if (valorCidade == null) {
      this.estadoCidade = '';
      return;
    }
    const cidadeSelecionada = this.cidadeList.find(cidade => cidade.value === valorCidade);
    if (cidadeSelecionada) {
      this.estadoCidade = cidadeSelecionada.estado;
    }
  }

  loadCliente(id: number): void {
    this.pessoaService.getById('/pessoas', String(id)).subscribe((response: any) => {
      this.form = {
        id: response.id,
        nome: response.nome,
        fantasia: response.fantasia,
        ruc: response.ruc,
        ci: response.ci,
        email: response.email,
        ddi: response.ddi,
        telefone: response.telefone,
        endereco: response.endereco,
        numero: response.numero,
        categoriaCliente: Boolean(response.categoriaCliente),
        categoriaFornecedor: Boolean(response.categoriaFornecedor),
        categoriaVendedor: response.categoriaVendedor,
        cidade: {
          label: String(response.cidade?.nome), // Converte para string diretamente
          value: Number(response.cidade?.id), // Converte para número diretamente
        },
        tipoPessoa: this.tipoPessoaOptions.find(item => item.label == this.translate.instant(`enums.tipo_pessoa.${response.tipoPessoa?.enum?.toLowerCase()}`)),
        tipoNaturezaFiscal: this.tipoNaturezaFiscalOptions.find(item => item.label == this.translate.instant(`enums.tipo_natureza_fiscal.${response.tipoNaturezaFiscal?.enum?.toLowerCase()}`)),
        dataNascimento: response.dataNascimento
          ? new Date(response.dataNascimento.split('/').reverse().join('-')).toISOString().split('T')[0]
          : '', // Converte DD/MM/YYYY para Date e depois para string
        sexo: this.sexoOptions.find(item => item.label === this.translate.instant(`enums.sexo.${response.sexo?.enum?.toLowerCase()}`)),
      };
      this.estadoCidade = response.cidade?.estado?.nome;

      // Converter DDI string para PaisDDI
      const ddiComPlus = '+' + (response.ddi || '595');
      this.paisSelecionado = PAISES_DDI.find(p => p.ddi === ddiComPlus) || PAISES_DDI[0];
    });
  }

  loadDropdowns(): void {
    // Carregar as cidades
    this.cidadeService.get('/cidade?listarTodos=true').subscribe((response) => {
      this.cidadeList = response.content.map((cidade: any) => ({
        label: cidade.nome,
        value: String(cidade.id),
        estado: cidade.nomeEstado
      }));
    });
  }

  getDropItems<T extends object>(enumType: T): { label: string; value: T[keyof T] }[] {
    return Object.values(enumType).map((item) => ({
      label: String(item),
      value: item as T[keyof T],
    }));
  }

  applyMask(event: any) {
    let value = event.target.value.replace(/\D/g, ''); // Remove todos os caracteres não numéricos

    if (value.length > 12) {
      value = value.substring(0, 12);
    }

    if (value.length > 6) {
      value = value.replace(/^(\d{3})(\d{3})(\d{3})(\d{0,2})$/, '($1) $2-$3$4');
    } else if (value.length > 3) {
      value = value.replace(/^(\d{3})(\d{0,3})$/, '($1) $2');
    } else if (value.length > 0) {
      value = value.replace(/^(\d{0,3})$/, '($1');
    } else {
      value = '';
    }

    event.target.value = value;
    this.form.telefone = value;
  }

  onDateInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const valor = input.value;
    if (valor) {
      const partes = valor.split('-');
      if (partes.length > 0 && partes[0].length > 4) {
        // Truncar o ano para 4 digitos
        partes[0] = partes[0].substring(0, 4);
        const novaData = partes.join('-');
        input.value = novaData;
        this.form.dataNascimento = novaData;
      }
    }
  }

  validarCI(): boolean {
    const ci = (this.form.ci || '').trim();
    if (!ci) return true; // CI é opcional
    if (!/^\d+$/.test(ci)) return false; // Apenas dígitos
    return ci.length >= 5 && ci.length <= 8;
  }

  validarDataNascimento(): boolean {
    if (!this.form.dataNascimento) {
      return true; // Campo opcional, vazio e valido
    }
    const partes = this.form.dataNascimento.split('-');
    if (partes.length > 0 && partes[0].length > 4) {
      return false; // Ano com mais de 4 digitos
    }
    return true;
  }

  submit(): void {
    if (this.submitting) return;
    if (!this.form.tipoPessoa || !this.form.tipoNaturezaFiscal || !this.form.sexo || !this.form.nome) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('cliente-edit.mensagens.campos_obrigatorios'),
      });
      return;
    }

    if (!this.validarCI()) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('cliente-edit.validacoes.ci_invalido'),
      });
      return;
    }

    if (!this.validarDataNascimento()) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('erro'),
        detail: this.translate.instant('cliente-edit.validacoes.data_nascimento_invalida'),
      });
      return;
    }

    const body: PessoaDTO = {
      nome: this.form.nome,
      fantasia: this.form.fantasia != '' ? this.form.fantasia : undefined,
      ruc: this.form.ruc || undefined,
      ci: this.form.ci || undefined,
      email: this.form.email,
      ddi: this.paisSelecionado.ddi.replace('+', '') || '595',
      telefone: this.form.telefone?.replace(/\D/g, '') || undefined,
      endereco: this.form.endereco,
      numero: this.form.numero,
      cidade_id: Number(this.form.cidade?.value),
      categoriaCliente: this.form.categoriaCliente,
      categoriaFornecedor: this.form.categoriaFornecedor,
      categoriaVendedor: this.form.categoriaVendedor,
      tipoPessoa: this.form.tipoPessoa.value,
      tipoNaturezaFiscal: this.form.tipoNaturezaFiscal.value,
      dataNascimento: this.form.dataNascimento,
      sexo: this.form.sexo.value,
    };

    this.submitting = true;
    this.pessoaService.put(`/pessoas/${this.form.id}`, body).subscribe({
      next: () => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('cliente-edit.mensagens.cliente_editado'),
        });
        this.navigateListaDeClientes();
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  createEmptyForm(): PessoaFormDTO {
    return {
      nome: '',
      fantasia: '',
      ruc: '',
      ci: '',
      email: '',
      ddi: '',
      telefone: '',
      endereco: '',
      numero: '',
      cidade: undefined,
      categoriaCliente: true,
      categoriaFornecedor: false,
      categoriaVendedor: false,
      tipoPessoa: undefined,
      tipoNaturezaFiscal: undefined,
      dataNascimento: '',
      sexo: undefined,
    };
  }

  onCiModelChange(value: string): void {
    const apenasDigitos = (value || '').replace(/\D/g, '').substring(0, 8);
    if (value !== apenasDigitos) {
      this.form.ci = apenasDigitos;
    }
  }

  onCiInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const apenasDigitos = (input.value || '').replace(/\D/g, '').substring(0, 8);
    if (input.value !== apenasDigitos) {
      input.value = apenasDigitos;
      this.form.ci = apenasDigitos;
    }
  }

  onTipoPessoaChange(): void {
    if (this.form.tipoPessoa?.value === TipoPessoa.FISICA) {
      this.form.ruc = '';
    } else if (this.form.tipoPessoa?.value === TipoPessoa.JURIDICA) {
      this.form.ci = '';
    }
  }

  isPessoaFisica(): boolean {
    return this.form.tipoPessoa?.value === TipoPessoa.FISICA;
  }

  navigateListaDeClientes() {
    this.router.navigate(['lista-de-clientes']);
  }
}
